import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  TextField,
  Stack,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Tooltip,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { useStatus } from "../../popup/NotificationContext";
import {
  useAddReminderMutation,
  useDeleteReminderMutation,
  useGetRemindersQuery,
} from "../../redux/logistics/logisticsApi";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    backgroundColor: "#fff",
  },
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB");
};

const buildCsv = (rows) => {
  const headers = ["Name", "Type", "Start", "Period", "Date"];
  const body = rows.map((row) =>
    [row.name, row.type, row.start, row.period, row.date]
      .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
      .join(",")
  );
  return [headers.join(","), ...body].join("\n");
};

const ReminderPortalModal = ({ open = true, onClose }) => {
  const { showStatus } = useStatus();
  const { data: remindersPayload = [], refetch } = useGetRemindersQuery();
  const [addReminder] = useAddReminderMutation();
  const [deleteReminder] = useDeleteReminderMutation();

  const [searchQuery, setSearchQuery] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [modalMode, setModalMode] = useState("table");
  const [formData, setFormData] = useState({
    name: "",
    type: "",
    start: "",
    period: "",
    date: "",
  });

  const reminders = useMemo(
    () =>
      (Array.isArray(remindersPayload) ? remindersPayload : []).map((item) => ({
        ...item,
        name: item?.name || "-",
        type: item?.type || "-",
        start: item?.start ? formatDate(item.start) : "-",
        period: item?.period || "-",
        date: formatDate(item?.date),
      })),
    [remindersPayload]
  );

  const filteredReminders = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return reminders;
    return reminders.filter((item) =>
      [item.name, item.type, item.period, item.start, item.date].some((value) =>
        String(value || "").toLowerCase().includes(query)
      )
    );
  }, [reminders, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredReminders.length / rowsPerPage));
  const pagedReminders = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredReminders.slice(start, start + rowsPerPage);
  }, [filteredReminders, page, rowsPerPage]);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const resetForm = () => {
    setFormData({
      name: "",
      type: "",
      start: "",
      period: "",
      date: "",
    });
  };

  const handleDownloadCsv = () => {
    const csv = buildCsv(filteredReminders);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "reminders.csv";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    showStatus("success", "Reminders CSV downloaded.");
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.type.trim() || !formData.date) {
      showStatus("warning", "Name, type, aur date required hain.");
      return;
    }
    try {
      await addReminder({
        ...formData,
        start: formData.start || null,
      }).unwrap();
      await refetch();
      resetForm();
      setModalMode("table");
      setPage(1);
      showStatus("success", "Reminder added successfully.");
    } catch (error) {
      showStatus("error", error?.data?.error || "Failed to add reminder.");
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;
    if (!window.confirm("Reminder delete karna hai?")) return;
    try {
      await deleteReminder(id).unwrap();
      showStatus("success", "Reminder deleted.");
    } catch (error) {
      showStatus("error", error?.data?.error || "Failed to delete reminder.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" PaperProps={{ sx: { borderRadius: 2, overflow: "hidden", minHeight: "72vh" } }}>
      <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>
        <Box sx={{ borderTop: "4px solid #4f9cf9", display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 2 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 800 }}>
            {modalMode === "table" ? "Reminders List" : "Add Reminders"}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {modalMode === "table" ? (
          <>
            <Box sx={{ px: 3, py: 2.5, borderTop: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb" }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "360px auto" }, gap: 1.5, alignItems: "center" }}>
                <TextField
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  sx={inputSx}
                  InputProps={{ endAdornment: <SearchIcon style={{ color: "#fb7185" }} /> }}
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <IconButton onClick={() => setModalMode("create")} sx={{ border: "2px solid #111827", borderRadius: 0.5, color: "#111827" }}>
                    <AddIcon />
                  </IconButton>
                </Stack>
              </Box>
            </Box>

            <Box sx={{ px: 3, py: 1.5, borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select value={rowsPerPage} onChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(1); }}>
                  {[10, 25, 50, 100].map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Stack direction="row" spacing={1}>
                <IconButton onClick={() => refetch()} sx={{ color: "#6b7280" }}>
                  <RefreshIcon />
                </IconButton>
                <IconButton onClick={handleDownloadCsv} sx={{ color: "#6b7280" }}>
                  <DownloadIcon />
                </IconButton>
              </Stack>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ maxHeight: "52vh", borderRadius: 0 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {["Name", "Type", "Start", "Period", "Date", "Action"].map((head) => (
                      <TableCell key={head} sx={{ fontWeight: 700, color: "#6b7280", bgcolor: "#fff", borderBottom: "1px solid #d1d5db" }}>
                        {head}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedReminders.length ? (
                    pagedReminders.map((item) => (
                      <TableRow key={item._id} hover>
                        <TableCell sx={{ fontWeight: 700 }}>{item.name}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{item.type}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{item.start}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{item.period}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{item.date}</TableCell>
                        <TableCell>
                          <Tooltip title="Delete Reminder">
                            <IconButton size="small" onClick={() => handleDelete(item._id)} sx={{ bgcolor: "#ef4444", borderRadius: 1 }}>
                              <DeleteOutlineIcon sx={{ color: "#fff", fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 12, color: "#6b7280" }}>
                        <Stack spacing={1} alignItems="center">
                          <Box sx={{ width: 92, height: 92, borderRadius: "50%", bgcolor: "#fff1f2", display: "grid", placeItems: "center", color: "#fb7185", fontSize: 34 }}>
                            ✕
                          </Box>
                          <Typography sx={{ fontWeight: 700 }}>No Data Available In Table</Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography sx={{ color: "#374151" }}>
                Showing {filteredReminders.length ? (page - 1) * rowsPerPage + 1 : 0} to {Math.min(page * rowsPerPage, filteredReminders.length)} of {filteredReminders.length} entries
              </Typography>
              <Pagination page={page} count={totalPages} onChange={(_, value) => setPage(value)} shape="rounded" />
            </Box>
          </>
        ) : (
          <Box sx={{ borderTop: "1px solid #e5e7eb" }}>
            <Box sx={{ px: 3, py: 2.5 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2.5 }}>
                <Box>
                  <Typography sx={{ mb: 1, fontSize: 18, fontWeight: 500 }}>Name</Typography>
                  <TextField
                    fullWidth
                    value={formData.name}
                    onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
                    sx={inputSx}
                  />
                </Box>
                <Box>
                  <Typography sx={{ mb: 1, fontSize: 18, fontWeight: 500 }}>Type</Typography>
                  <TextField
                    fullWidth
                    value={formData.type}
                    onChange={(event) => setFormData((prev) => ({ ...prev, type: event.target.value }))}
                    sx={inputSx}
                  />
                </Box>
                <Box>
                  <Typography sx={{ mb: 1, fontSize: 18, fontWeight: 500 }}>Start</Typography>
                  <TextField
                    fullWidth
                    value={formData.start}
                    onChange={(event) => setFormData((prev) => ({ ...prev, start: event.target.value }))}
                    sx={inputSx}
                  />
                </Box>
                <Box>
                  <Typography sx={{ mb: 1, fontSize: 18, fontWeight: 500 }}>Period</Typography>
                  <TextField
                    fullWidth
                    value={formData.period}
                    onChange={(event) => setFormData((prev) => ({ ...prev, period: event.target.value }))}
                    sx={inputSx}
                  />
                </Box>
                <Box sx={{ maxWidth: { md: 620 } }}>
                  <Typography sx={{ mb: 1, fontSize: 18, fontWeight: 500 }}>Date</Typography>
                  <TextField
                    fullWidth
                    type="date"
                    value={formData.date}
                    onChange={(event) => setFormData((prev) => ({ ...prev, date: event.target.value }))}
                    sx={inputSx}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </Box>
            </Box>

            <Box sx={{ px: 3, pb: 2.5, display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                sx={{
                  minWidth: 220,
                  bgcolor: "#f43f5e",
                  "&:hover": { bgcolor: "#e11d48" },
                  fontWeight: 800,
                  borderRadius: 1.2,
                }}
              >
                SUBMIT
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  resetForm();
                  setModalMode("table");
                }}
                sx={{
                  minWidth: 220,
                  bgcolor: "#6b7280",
                  "&:hover": { bgcolor: "#4b5563" },
                  fontWeight: 800,
                  borderRadius: 1.2,
                }}
              >
                CANCEL
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReminderPortalModal;
